import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import {
  Breed,
  Country,
  CirculationZone,
  PetQuestion,
  CatalogResponse,
} from '../../../domain/interfaces/initial-data/initial-data.interface';
import { AuthService } from '../auth/auth.service';
import { InitialDataRepository } from '../../../domain/repositories/initial-data/initial-data.repository';

/**
 * Servicio de datos iniciales
 * Gestiona las peticiones al endpoint de catálogo inicial
 * (países, zonas, razas, preguntas de mascotas)
 */
@Injectable({
  providedIn: 'root',
})
export class InitialDataService extends InitialDataRepository {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);

  private baseUrl = environment.apiBaseUrl;
  private initialDataEndpoint = `${this.baseUrl}${environment.endpoints.initialData}`;


  constructor(){
    super();
    this.getAllInitialData()
  }
  /**
   * Obtiene todos los datos del catálogo inicial (países, zonas, razas, preguntas)
   * @returns Observable con la respuesta completa del catálogo
   */
  getAllInitialData(): Observable<CatalogResponse> {
    return this.authService.getAuthHeaders().pipe(
      switchMap((headers) =>
        this.http.get<CatalogResponse>(this.initialDataEndpoint, { headers }),
      ),
      catchError((error) => {
        console.error('[InitialDataService] Error obteniendo datos iniciales:', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Obtiene la lista de países activos
   * @returns Observable con array de países
   */
  getCountries(): Observable<Country[]> {
    return this.getAllInitialData().pipe(
      map((response) =>
        response.data.paises.data.countries.map((c) => ({
          id: c.Id ?? c.id,
          value: c.value,
          label: c.label,
        })),
      ),
      catchError((error) => {
        console.error('[InitialDataService] Error obteniendo países:', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Obtiene las zonas de circulación
   * @returns Observable con array de zonas
   */
  getCirculationZones(): Observable<CirculationZone[]> {
    return this.getAllInitialData().pipe(
      map((response) => response.data.zonas.data.circulation_zones),
      catchError((error) => {
        console.error('[InitialDataService] Error obteniendo zonas:', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Obtiene las razas de mascotas
   * @returns Observable con array de razas
   */
  getBreeds(): Observable<Breed[]> {
    return this.getAllInitialData().pipe(
      map((response) => response.data.razas.breeds),
      catchError((error) => {
        console.error('[InitialDataService] Error obteniendo razas:', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Obtiene las preguntas para mascotas
   * @returns Observable con array de preguntas
   */
  getPetQuestions(): Observable<PetQuestion[]> {
    return this.getAllInitialData().pipe(
      map((response) => response.data.preguntas_mascotas.questions),
      catchError((error) => {
        console.error('[InitialDataService] Error obteniendo preguntas de mascotas:', error);
        return throwError(() => error);
      }),
    );
  }
}